import { IconNames } from '@blueprintjs/icons';
import {
    DateFormats,
    formatDate,
    formatNameLastFirst,
    useDebouncedFunction,
} from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { ReferralChatIcon } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    GridWithFilters,
    PrimaryData,
    SecondaryData,
    SupplierCell,
    TimeFrameOptions,
} from '@strata/react-grid';
import { FirstFillStatusIcon, Icon } from '@strata/react-status-icons';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { ReferralAndOnboardingStatusIndicator } from 'components/referrals/ReferralAndOnboardingStatusIndicator';
import { ReferralRowMenu } from 'components/referrals/ReferralRowMenu';
import { useUnreadCounts } from 'services/APIHooks';
import { isReferralRowDisabled } from 'services/gridHelpers';
import { AppRoutes } from 'services/routes';
import { useGetPagedReferralsQuery, useGetReferralCountQuery } from 'store/api/agoraMonitorApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';
import { fetchReferralFilterCategories } from 'store/referrals/referralsSlice';

const initialSortProperties = [{ id: 'submitted_at', desc: true }];

const timeFrameConfig = {
    key: 'timeFrame',
    defaultValue: TimeFrameOptions.PAST_MONTH,
    optionOverrideMap: {
        [TimeFrameOptions.PAST_DAY]: { text: 'Past 24 Hours' },
        ALL_TIME: { text: 'All Time' },
    },
};

const columns = ({ payerSegmentMap, referralConversationCounts }) => [
    {
        accessorKey: 'friendly_id',
        header: 'Referral ID',
        size: 160,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'patient',
        header: 'Patient',
        size: 110,
        cell: ({ row }) => (
            <SecondaryData
                data={formatNameLastFirst(
                    row.original.patient_last_name,
                    row.original.patient_first_name,
                )}
            />
        ),
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'supplier_name',
        header: 'Servicing Supplier',
        size: 135,
        cell: ({ getValue, row }) => (
            <SupplierCell supplierName={getValue()} lite={row.original.lite} />
        ),
    },
    {
        accessorKey: 'product_service_display_name',
        header: 'Product',
        size: 110,
    },
    {
        accessorKey: 'segment_id',
        header: 'Health Plan',
        enableSorting: false,
        size: 110,
        cell: ({ getValue }) => (
            <SecondaryData data={payerSegmentMap?.[getValue()]?.segment_name} />
        ),
    },
    {
        accessorKey: 'route_name',
        header: 'Route Rule',
        size: 80,
    },
    {
        accessorKey: 'needs_by_date',
        header: 'Next Fill NBD',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'first_fill_status',
        enableSorting: false,
        header: () => (
            <Icon
                icon={IconNames.HIGH_PRIORITY}
                tooltipContent={
                    <div>
                        <div>First fill status indicator.</div>
                        <div>1. Pending First Fill</div>
                        <div>2. First Fill in Progress</div>
                        <div>3. Claim Received</div>
                        <div>4. Claim From Wrong Pharmacy</div>
                    </div>
                }
            />
        ),
        size: 45,
        cell: ({ getValue }) => <FirstFillStatusIcon status={getValue()} />,
    },
    {
        accessorKey: 'referral_id',
        header: () => (
            <Icon
                icon={IconNames.CHAT}
                tooltipContent="Open conversations and unread messages indicator."
            />
        ),
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) => (
            <ReferralChatIcon
                openConversations={referralConversationCounts?.[getValue()]?.open}
                unreadMessages={referralConversationCounts?.[getValue()]?.unread}
            />
        ),
    },
    {
        accessorKey: 'submitted_at',
        header: 'Received Date',
        size: 140,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'referral_status',
        header: 'Referral Status',
        size: 145,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <ReferralAndOnboardingStatusIndicator
                referralStatus={getValue().trim()}
                onboardingStatus={row.original.onboarding_status}
                enrollmentStatus={row.original.enrollment_status}
                skipFetch
            />
        ),
    },
    {
        accessorKey: 'treatment_id',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <ReferralRowMenu treatmentId={getValue()} payerId={row.original.payer_id} />
        ),
    },
];

export const ReferralsListPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = data ?? {};

    const { referralConversationCounts } = useUnreadCounts();

    const timeFrameFilter = useSelector(
        (state) =>
            state.grids.referrals.activeFilters?.timeFrame?.[0] ?? TimeFrameOptions.PAST_MONTH,
    );

    const referralFilterCategories = useSelector(
        (state) => state.referrals.referralFilterCategories,
    );
    useEffect(() => {
        if (timeFrameFilter && payerSegmentMap) {
            dispatch(fetchReferralFilterCategories(timeFrameFilter, payerSegmentMap));
        }
    }, [dispatch, payerSegmentMap, timeFrameFilter]);

    const {
        activeFilters,
        filterPaneWidth,
        page,
        search,
        sort,
        updateCurrentPage,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
    } = useGridState(Grids.REFERRALS, initialSortProperties);

    // The search box updates `search` on every keystroke to keep the input responsive,
    // but the referrals queries are expensive, so only feed the API a debounced copy —
    // otherwise each keystroke fires a concurrent query and saturates the DB.
    const debounceSearchText = useDebouncedFunction();
    const [debouncedSearchText, setDebouncedSearchText] = useState(search);

    const filterBody = useMemo(
        () => ({
            additional_filters: activeFilters,
            sort,
        }),
        [activeFilters, sort],
    );

    const { data: referralCountData, refetch: refetchReferralCount } = useGetReferralCountQuery({
        search: debouncedSearchText,
        timeFrameFilter,
        filterBody,
    });

    const {
        data: currentPageData,
        isFetching: isCurrentPageDataFetching,
        refetch: refetchCurrentPageData,
        fulfilledTimeStamp: lastUpdatedTimestamp,
    } = useGetPagedReferralsQuery({
        page,
        search: debouncedSearchText,
        timeFrameFilter,
        filterBody,
    });

    return (
        <FormPageContainer>
            <GridWithFilters
                activeFilters={activeFilters}
                columns={columns({
                    payerSegmentMap,
                    referralConversationCounts,
                })}
                currentPage={page}
                data={currentPageData?.referrals}
                enableSearch
                filterCategories={referralFilterCategories}
                filterPaneRenderWidth={filterPaneWidth}
                isLoading={isCurrentPageDataFetching || search !== debouncedSearchText}
                manualSorting
                onFilter={updateGridActiveFilters}
                onFilterPaneResize={updateFilterPaneWidth}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) => {
                    if (!isReferralRowDisabled(rowData.referral_status)) {
                        navigate(AppRoutes.referralDetails(rowData.payer_id, rowData.referral_id));
                    }
                }}
                onSearch={(value) => {
                    updateGridSearch(value);
                    debounceSearchText(() => setDebouncedSearchText(value));
                }}
                onSort={updateGridSort}
                rowCssClass={(rowData) =>
                    classNames({
                        'griddle-row--disabled': isReferralRowDisabled(rowData.referral_status),
                    })
                }
                searchText={search}
                sortProperties={sort}
                timeFrameConfig={timeFrameConfig}
                topRightContent={
                    <div className="sds_referral-grid__refresh">
                        {lastUpdatedTimestamp && (
                            <span className="sds_referral-grid__refresh__timestamp">
                                {`Last Updated ${formatDate(lastUpdatedTimestamp, DateFormats.DATE_TIME)}`}
                            </span>
                        )}
                        <ActionButtonText
                            label="Refresh"
                            leftIcon={IconNames.REFRESH}
                            onClick={() => {
                                refetchCurrentPageData();
                                refetchReferralCount();
                            }}
                        />
                    </div>
                }
                totalCount={referralCountData?.count}
            />
        </FormPageContainer>
    );
};
